import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { Search } from "lucide-react";
import SpotIncentiveDateRangePicker from "./SpotIncentiveDateRangePicker.jsx";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getEligibleResourceForSpotIncentive } from "@/other/utils/incentiveUtils.jsx";
import { DateHelper } from "@bryntum/schedulerpro";

interface ResourceSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedResourceIds: number[];
  onResourcesSelect: (data: { resourceIds: number[]; startDate: string; endDate: string; multiplyByDays: boolean }) => void;
  selectedTaskId: number | null;
  editorInstance?: unknown;
  multiplyByDays: boolean;
}

const SpotIncentiveResourceSelector = ({ open, onOpenChange, selectedResourceIds, onResourcesSelect, selectedTaskId, editorInstance, multiplyByDays }: ResourceSelectorProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedResourceIds);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [localMultiplyByDays, setLocalMultiplyByDays] = useState<boolean>(multiplyByDays);
  const eventRecordMap = useRef(new Map());

  // Timeline dates calculation like ResourceStep
  const timelineDates = useMemo(() => {
    const now = new Date();
    const timelineStartDate = new Date(now);
    timelineStartDate.setMonth(timelineStartDate.getMonth() - 3);
    const timelineEndDate = new Date(now);
    return { timelineStartDate, timelineEndDate };
  }, []);

  const handleDateRangeChange = (selectedStartDate: Date, selectedEndDate: Date) => {
    setStartDate(selectedStartDate.toISOString());
    setEndDate(selectedEndDate.toISOString());
  };

  // Get eligible resources based on task and date range
  const eligibleResources = useMemo(() => {
    if (!selectedTaskId || !project || !startDate || !endDate) return [];

    try {
      const events = getEligibleResourceForSpotIncentive({
        taskId: selectedTaskId,
        project,
        startDate: new Date(startDate),
        endDate: DateHelper.add(endDate, 1, "day")
      });

      // Update eventRecordMap
      events.forEach(({ record, timeSpent }) =>
        eventRecordMap.current.set(Number(record.resourceId), { record, timeSpent })
      );

      return events.map(({ record }) => ({
        resourceId: Number(record.resourceId), // Ensure integer
        name: record.resource?.name || 'Unknown',
        resource: record.resource
      }));
    } catch (error) {
      return [];
    }
  }, [selectedTaskId, project, startDate, endDate]);

  // Update local state when dialog opens
  useEffect(() => {
    if (open && eligibleResources.length > 0) {
      // Auto-select all eligible resources if none selected
      const autoSelectedIds = selectedResourceIds.length === 0
        ? eligibleResources.map(r => r.resourceId)
        : selectedResourceIds;
      setLocalSelectedIds(autoSelectedIds);
      setLocalMultiplyByDays(multiplyByDays);
    } else if (open) {
      setLocalSelectedIds(selectedResourceIds);
      setLocalMultiplyByDays(multiplyByDays);
    }
  }, [open, selectedResourceIds, eligibleResources, multiplyByDays]);

  // Update local state when dialog opens or closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset local state when closing without saving
      setLocalSelectedIds(selectedResourceIds);
      setLocalMultiplyByDays(multiplyByDays);
    }
    onOpenChange(isOpen);
  };

  const filteredResources = eligibleResources.filter(resource =>
    resource.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleResourceToggle = (resourceId: number) => {
    const newSelection = localSelectedIds.includes(resourceId)
      ? localSelectedIds.filter(id => id !== resourceId)
      : [...localSelectedIds, resourceId];

    setLocalSelectedIds(newSelection);
  };

  const handleSave = () => {
    onResourcesSelect({
      resourceIds: localSelectedIds,
      startDate: startDate || new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: endDate || new Date().toISOString(),
      multiplyByDays: localMultiplyByDays
    });
    onOpenChange(false);
  };

  const MODAL_SIZE_CONFIG = {
    width: "48rem",
    height: "46rem",
    minWidth: "min(90vw, 48rem)",
    minHeight: "min(90vh, 46rem)",
    maxWidth: "90vw",
    maxHeight: "90vh"
  };

  if (!open) return null;

  return (
    <BaseModal
      title="Select Resources"
      description="Select resources by date range and filter"
      onClose={() => handleOpenChange(false)}
      size={MODAL_SIZE_CONFIG}
      editorInstance={editorInstance}
      record={{}}
      headerIconCls=""
      modal={false}
      version=""
      swivuAssistanceConfig={null}
      hideMenu={true}
      footerRenderer={() => (
        <div className="flex justify-end items-center pt-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} className="text-sm">
              Cancel
            </Button>
            <Button onClick={handleSave} className="text-sm">
              Done
            </Button>
          </div>
        </div>
      )}
    >
      <BaseModalBody>
        <div className="space-y-4">
          {/* Date Range */}
          <div className="space-y-2">
            <SpotIncentiveDateRangePicker
              timelineStartDate={timelineDates.timelineStartDate}
              timelineEndDate={timelineDates.timelineEndDate}
              selectedStartDate={startDate ? new Date(startDate) : null}
              selectedEndDate={endDate ? new Date(endDate) : null}
              onChange={handleDateRangeChange}
            />
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm placeholder:text-sm"
            />
          </div>

          {/* Resources List */}
          <ScrollArea className="h-64">
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Eligible Resources ({filteredResources.length}):
              </div>
              {filteredResources.length > 0 ? (
                filteredResources.map((resource) => {
                  const eventData = eventRecordMap.current.get(resource.resourceId);
                  const timeSpent = eventData?.timeSpent || 0;
                  const daysWorked = timeSpent % 1 === 0 ? timeSpent : timeSpent.toFixed(2);
                  return (
                    <div
                      key={resource.resourceId}
                      className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-secondary/50 transition-colors cursor-pointer"
                      onClick={() => handleResourceToggle(resource.resourceId)}
                    >
                      <Checkbox
                        checked={localSelectedIds.includes(resource.resourceId)}
                        onCheckedChange={() => handleResourceToggle(resource.resourceId)}
                      />
                      <div className="flex-1 flex justify-between items-center">
                        <div className="text-sm font-medium">{resource.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {daysWorked} Day{daysWorked != 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : selectedTaskId ? (
                <div className="text-center p-6 border rounded-lg border-dashed">
                  <p className="text-muted-foreground text-sm">
                    No resources found for the selected task and date range.
                  </p>
                </div>
              ) : (
                <div className="text-center p-6 border rounded-lg border-dashed">
                  <p className="text-muted-foreground text-sm">
                    Please select a task first to see eligible resources.
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Multiply by days checkbox */}
          <div
            className="flex items-start space-x-3 p-3 rounded-lg border bg-card absolute bottom-4 left-4 right-4 cursor-pointer hover:bg-secondary/50 transition-colors"
            onClick={() => setLocalMultiplyByDays(!localMultiplyByDays)}
          >
            <Checkbox
              id="multiply-by-days"
              checked={localMultiplyByDays}
              onCheckedChange={(checked) => setLocalMultiplyByDays(checked === true)}
            />
            <div className="flex-1 space-y-1">
              <div
                className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Multiply points by days-on-task
              </div>
              <p className="text-xs text-muted-foreground">
                Points are multiplied by each resource's days-on-task within the selected date range. Days outside this range are excluded from the multiplier.
              </p>
            </div>
          </div>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};

export default SpotIncentiveResourceSelector;
